import {useEffect,useRef,useState} from 'react';
import {Button} from '../ui/button';
import {ArrowLeft,Bell,BellOff,Camera,Lock,Reply,Search,Send,X} from 'lucide-react';
import {toast} from 'sonner';
import {configureDepartmentPush} from '../../services/departmentPush';
import TenantLogo,{primaryTenantDomain} from './TenantLogo';

const stamp=s=>new Date(s).toLocaleString(undefined,{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'});

// Stop requests when the view closes. Ignore older poll responses after a send.
function useFeed(client,url,background=false) {
  const [data,setData]=useState(null),[error,setError]=useState(false),[version,revise]=useState(0);
  useEffect(()=>{
    let live=true,inFlight=false;
    async function load(){
      if(inFlight||(document.hidden&&!background))return;
      inFlight=true;
      try{const r=await client.get(url);if(live){setData(r.data);setError(false);}}
      catch{if(live)setError(true);}
      finally{inFlight=false;}
    }
    load();const timer=setInterval(load,5000);document.addEventListener('visibilitychange',load);
    return ()=>{live=false;clearInterval(timer);document.removeEventListener('visibilitychange',load);};
  },[client,url,version,background]);
  return [data,error,()=>revise(v=>v+1)];
}

// Only generic alerts leave the workspace; never send payment details to the lock screen.
export function DepartmentTab({client,finance=false,scope,active,onOpen,onUnread}) {
  const storage='department-alerts:'+scope;
  const [enabled,setEnabled]=useState(()=>{try{return localStorage.getItem(storage)==='on';}catch{return false;}});
  const [working,setWorking]=useState(false),[issue,setIssue]=useState('');
  const operation=useRef(Promise.resolve()),changing=useRef(false);
  function configure(on,interactive,token){
    const req=operation.current.catch(()=>{}).then(()=>configureDepartmentPush(client,finance,on,interactive,token));
    operation.current=req;return req;
  }
  useEffect(()=>{
    if(!enabled)return;
    let live=true;
    const sync=e=>!changing.current&&configure(true,false,e?.detail?.token).then(()=>{if(live)setIssue('');}).catch(()=>{
      if(live)setIssue('Background alerts not confirmed. Turn alerts off and on to retry.');
    });
    sync();window.addEventListener('staff-fcm-token',sync);window.addEventListener('online',sync);
    return ()=>{live=false;window.removeEventListener('staff-fcm-token',sync);window.removeEventListener('online',sync);};
  },[enabled,scope,client,finance]);
  const [data,error]=useFeed(client,finance?'/api/finance/finance-chat':'/api/admin/finance-chat/alerts',enabled);
  const known=useRef(null);
  const count=data?.threads?.reduce((n,t)=>n+(t.unread||0),0)||0;
  useEffect(()=>{onUnread?.(count);},[count,onUnread]);
  useEffect(()=>{
    if(!data)return;
    const prior=known.current, next=new Map(prior||[]);
    let notify=false;
    for(const t of data.threads||[]){
      if(prior&&t.unread&&t.incoming&&(!prior.has(t.tenant)||t.incoming>(prior.get(t.tenant)||'')))notify=true;
      if(t.incoming)next.set(t.tenant,t.incoming);
    }
    known.current=next;
    if(!enabled||!notify)return;
    const title='Finance department',body='New message in your team conversation.';
    if(!document.hidden)toast.message(title,{description:body});
    // The server sends the OS alert, including when this page is closed.
  },[data,enabled,finance]);
  async function toggle(){
    if(changing.current)return;changing.current=true;setWorking(true);
    try{
      await configure(!enabled,true);
      try{localStorage.setItem(storage,enabled?'off':'on');}catch{}
      setIssue('');setEnabled(!enabled);
    }catch(e){const msg=e.response?'Device registration not confirmed. Retry.':e.message||'Notification change not confirmed.';
      setIssue(msg);toast.error(msg);}
    finally{changing.current=false;setWorking(false);}
  }
  return <span className="inline-flex items-center"><button type="button" aria-pressed={active} onClick={onOpen}
    className={'min-h-11 rounded-lg px-2 text-sm font-semibold '+(active?'bg-white/15 text-white':'text-gray-300')}>
    {finance?'Team chat':'Finance department'}{count>0&&<span className="ml-1 text-amber-300"> · {count} unread</span>}</button>
    <button type="button" disabled={working} aria-label={'Finance chat notifications '+(enabled?'on':'off')} aria-pressed={enabled} title={issue||'Finance chat alerts: '+(enabled?'on':'off')} onClick={toggle}
      className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-200">{enabled?<Bell size={17}/>:<BellOff size={17}/>}</button>
    {issue&&<span role="status" title={issue} className="text-xs text-amber-300">Check alerts</span>}{error&&<span title="Chat status unavailable" aria-label="Chat status unavailable">—</span>}</span>;
}

async function preparePhoto(file){
  if(!['image/jpeg','image/png','image/webp'].includes(file.type)||file.size>12*1024*1024)throw Error();
  const url=URL.createObjectURL(file);
  try{
    const im=new Image();im.src=url;await im.decode();
    const scale=Math.min(1,1440/Math.max(im.width,im.height)),c=document.createElement('canvas');
    c.width=Math.max(1,Math.round(im.width*scale));c.height=Math.max(1,Math.round(im.height*scale));
    const x=c.getContext('2d');x.fillStyle='white';x.fillRect(0,0,c.width,c.height);x.drawImage(im,0,0,c.width,c.height);
    for(const quality of [.82,.65,.48]){
      const b64=c.toDataURL('image/jpeg',quality).split(',')[1];if(b64.length<=1000000)return b64;
    }throw Error();
  }finally{URL.revokeObjectURL(url);}
}

function PrivatePhoto({client,url,messageRef}){
  const [jpeg,setJpeg]=useState(''),[busy,setBusy]=useState(false),[failed,setFailed]=useState(false);
  const wrap=useRef(null),dialog=useRef(null),[attempt,retry]=useState(0);
  useEffect(()=>{
    let live=true,started=false;
    async function load(){
      if(started)return;started=true;setBusy(true);setFailed(false);
      try{const r=await client.post(url+'/photo',{ref:messageRef});if(live)setJpeg(r.data.jpeg);}
      catch{if(live)setFailed(true);}finally{if(live)setBusy(false);}
    }
    if(!window.IntersectionObserver||attempt){load();return ()=>{live=false;};}
    const io=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){io.disconnect();load();}});
    io.observe(wrap.current);return ()=>{live=false;io.disconnect();};
  },[client,url,messageRef,attempt]);
  return <div ref={wrap} className="min-h-11">{jpeg?<><button type="button" aria-label="View photo" className="block max-w-full overflow-hidden rounded-lg border-0 bg-transparent p-0" onClick={()=>dialog.current?.showModal()}>
    <img className="max-h-56 max-w-full rounded-lg object-contain" alt="Chat attachment" src={'data:image/jpeg;base64,'+jpeg}/></button>
    <dialog ref={dialog} aria-label="Full-size chat photo" className="m-auto max-h-[92dvh] max-w-[94vw] rounded-xl bg-[#0B0E14] p-2 text-white backdrop:bg-black/75">
      <button type="button" className="block min-h-11 px-2" onClick={()=>dialog.current?.close()}>Close photo</button>
      <img alt="Full-size attachment" className="max-h-[77dvh] max-w-full object-contain" src={'data:image/jpeg;base64,'+jpeg}/></dialog></>:
    <button type="button" disabled={busy} className="min-h-11 underline" onClick={()=>retry(n=>n+1)}>{failed?'Photo unavailable · Retry':'Loading photo…'}</button>}</div>;
}

function ChatAvatar({brand,small=false}){
  const size=small?'h-7 w-7':'h-11 w-11';
  const domain=brand&&primaryTenantDomain(brand);
  const useTeamHost=domain&&/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)&&/^\/(api\/)?uploads\//.test(brand.chat_logo_path||'');
  const profile=useTeamHost?{...brand,logo_url:'https://'+domain+brand.chat_logo_path}:brand;
  return profile?<TenantLogo key={profile.id+':'+(profile.logo_url||profile.intro_logo_url||profile.icon_url||'')} tenant={profile}
    className={size+' !rounded-full'} imageClassName="object-contain" />:
    <img src="/finance-department.svg" alt="Finance department" width={small?28:44} height={small?28:44} className={size+' shrink-0 rounded-full'}/>;
}

export function DepartmentThread({client,tenant,finance=false,brand,onBack}) {
  const url=finance?'/api/finance/finance-chat/'+encodeURIComponent(tenant):'/api/admin/finance-chat';
  const [data,error,refresh]=useFeed(client,url);
  const [draft,setDraft]=useState(''),[sending,setSending]=useState(false),[sendError,setSendError]=useState(false);
  const [photo,setPhoto]=useState(''),[preparing,setPreparing]=useState(false),[photoError,setPhotoError]=useState(false),[reply,setReply]=useState(null);
  const [readError,setReadError]=useState(false);
  const list=useRef(null),follow=useRef(true),intent=useRef(null),lock=useRef(false),attachment=useRef(null),input=useRef(null);
  const rows=data?.messages||[],last=rows.at(-1)?.ref,side=finance?'finance':'team';
  const unseen=rows.filter(r=>r.side!==side&&!r.read_at);
  useEffect(()=>{
    if(!last||!unseen.length)return;
    let live=true,pending=false;
    async function mark(){
      if(!live||pending||document.hidden||!document.hasFocus()||!follow.current)return;
      pending=true;
      try{await client.post(url+'/seen',{ref:last});if(live){setReadError(false);refresh();}}
      catch{if(live)setReadError(true);}finally{pending=false;}
    }
    const log=list.current;
    mark();window.addEventListener('focus',mark);document.addEventListener('visibilitychange',mark);log?.addEventListener('scroll',mark);
    return ()=>{live=false;window.removeEventListener('focus',mark);document.removeEventListener('visibilitychange',mark);log?.removeEventListener('scroll',mark);};
  },[data,client,url,last,unseen.length]);
  useEffect(()=>{if(follow.current&&list.current)list.current.scrollTop=list.current.scrollHeight;},[last]);
  async function send(e){
    e.preventDefault();const text=draft.trim();if((!text&&!photo)||preparing||lock.current)return;
    lock.current=true;setSending(true);setSendError(false);
    try{
      intent.current??=crypto.randomUUID();
      await client.post(url,{text,intent:intent.current,...(photo?{photo}:{}),...(reply?{reply_to:reply.ref}:{})});
      intent.current=null;setDraft('');setReply(null);setPhoto('');follow.current=true;refresh();
    }catch{setSendError(true);}
    finally{lock.current=false;setSending(false);}
  }
  const partner=finance?(brand||{id:tenant,name:'Website team'}):null;
  return <section className="flex h-[min(72dvh,48rem)] min-h-72 min-w-0 flex-col overflow-hidden rounded-xl bg-[#151f30] text-white" aria-label="Finance department conversation">
    <header className="flex shrink-0 items-center gap-3 border-b border-white/10 px-3 py-3 sm:px-4">
      {onBack&&<button type="button" aria-label="Back to teams" className="flex h-11 w-11 items-center justify-center rounded-full border-0 bg-white/5 md:hidden" onClick={onBack}><ArrowLeft size={20}/></button>}
      <ChatAvatar brand={partner}/><div className="min-w-0"><h2 className="truncate text-lg font-semibold">{partner?.name||'Finance department'}</h2>
      <p className="flex items-center gap-1 text-xs text-gray-300"><Lock size={12}/> Private team conversation</p></div></header>
    {error&&<p role="alert">Chat could not refresh. <button className="min-h-11 underline" onClick={refresh}>Retry</button></p>}
    <div ref={list} role="log" aria-label="Messages" className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-4 sm:px-4"
      onScroll={e=>{const n=e.currentTarget;follow.current=n.scrollHeight-n.scrollTop-n.clientHeight<48;}}>
      {!data&&!error&&<p>Loading conversation…</p>}
      {data&&!rows.length&&<p className="text-sm text-gray-300">No messages yet. Start with a question or a payment reference.</p>}
      {rows.map(r=><div key={r.ref||r.side+r.id} className={'flex items-start gap-2 '+(r.side===side?'justify-end':'justify-start')}>
        {r.side!==side&&<ChatAvatar brand={partner} small/>}
        <div className={'flex min-w-0 max-w-[82%] flex-col '+(r.side===side?'items-end':'items-start')}>
        <span className="mb-1 px-1 text-xs text-gray-300">{r.name}</span>
        <div className={'min-w-0 max-w-full px-3.5 py-2 '+(r.side===side?'rounded-[18px_18px_7px_18px] bg-[#0084FF] text-white':'rounded-[18px_18px_18px_7px] bg-white text-gray-950')}>
        {r.reply&&<blockquote className="mb-1 break-words border-l border-current pl-2 text-sm"><strong>{r.reply.name}</strong> · {r.reply.excerpt}</blockquote>}
        {r.text&&<p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{r.text}</p>}
        {r.has_photo&&<PrivatePhoto client={client} url={url} messageRef={r.ref}/>}
        </div><div className="flex flex-wrap items-center gap-2 px-1 text-xs text-gray-300">
          <time dateTime={r.created}>{stamp(r.created)}</time>
          <span>{r.read_at?'Read':r.side===side?'Sent · Unread':'Unread'}</span>
          <button type="button" aria-label="Reply" title="Reply" disabled={sending||!r.ref} className="flex h-11 w-11 items-center justify-center rounded-full border-0 bg-transparent text-gray-300 hover:bg-white/5" onClick={()=>{setReply(r);intent.current=null;input.current?.focus();}}><Reply size={15}/></button></div></div>
      </div>)}
    </div>
    <form onSubmit={send} className="shrink-0 space-y-1 border-t border-white/10 p-2">
      {readError&&<p role="status">Read receipt not confirmed. The chat will retry.</p>}
      {reply&&<p className="break-words text-sm">Replying to {reply.name}: {reply.text?.slice(0,100)||'Photo'} <button type="button" disabled={sending} className="min-h-11 underline" onClick={()=>{setReply(null);intent.current=null;}}>Cancel reply</button></p>}
      {sendError&&<p role="alert" className="text-amber-300">Send not confirmed. Retry the same message to avoid a duplicate.</p>}
      <input type="file" aria-label="Attach photo" ref={attachment} className="hidden" accept="image/jpeg,image/png,image/webp" disabled={sending||preparing} onChange={async e=>{
        const file=e.target.files?.[0];e.target.value='';if(!file)return;
        setPreparing(true);setPhoto('');setPhotoError(false);intent.current=null;
        try{setPhoto(await preparePhoto(file));}catch{setPhotoError(true);}finally{setPreparing(false);}
      }}/>
      {photoError&&<p role="alert">Choose a smaller JPEG, PNG or WebP photo (up to 12 MB).</p>}
      {photo&&<div className="flex items-center gap-2"><img alt="Selected attachment" className="h-14 w-14 rounded object-contain" src={'data:image/jpeg;base64,'+photo}/>
        <button type="button" disabled={sending} className="min-h-11 underline" onClick={()=>{setPhoto('');intent.current=null;}}>Remove photo</button></div>}
      <div className="chat-composer flex items-end gap-1 rounded-[28px] border border-white/10 bg-[#0B0E14] p-1.5">
        <button type="button" aria-label="Add photo" title="Add photo" disabled={preparing||sending} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/5" onClick={()=>attachment.current?.click()}><Camera size={18}/></button>
        <textarea ref={input} aria-label="Message" className="chat-composer-input min-h-11 min-w-0 flex-1 resize-none border-0 bg-transparent px-2 py-3 text-base leading-5 text-white" rows={1} maxLength={2000} disabled={sending} value={draft}
          onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey&&!e.nativeEvent.isComposing){e.preventDefault();e.currentTarget.form.requestSubmit();}}}
          onChange={e=>{setDraft(e.target.value);intent.current=null;setSendError(false);}} placeholder="Type a message…"/>
        <Button type="submit" aria-label={sendError?'Retry send':'Send message'} disabled={(!draft.trim()&&!photo)||sending||preparing}
          className="chat-composer-send h-11 w-11 shrink-0 rounded-full bg-[#0084FF] p-0 text-white hover:bg-[#0A78E8]"><Send size={18}/></Button>
      </div>{(preparing||sending)&&<p role="status" className="text-xs text-gray-300">{preparing?'Preparing photo…':'Sending…'}</p>}
    </form></section>;
}

export default function FinanceDepartment({client,teams}) {
  const [selected,setSelected]=useState('');
  const [search,setSearch]=useState('');
  const [data,error,refresh]=useFeed(client,'/api/finance/finance-chat');
  const directory=new Map(teams.filter(t=>t.id&&!t.is_main).map(t=>[t.id,t]));
  const activity=new Map((data?.threads||[]).map(t=>[t.tenant,t]));
  const matching=[...directory.values()].filter(t=>(t.name||t.slug||'').toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a,b)=>(activity.get(b.id)?.created||'').localeCompare(activity.get(a.id)?.created||'')||(a.name||'').localeCompare(b.name||''));
  return <section className="space-y-3 text-white"><h2 className="text-2xl font-semibold">Team conversations</h2>
    {error&&<p role="alert">Inbox could not refresh. <button onClick={refresh} className="min-h-11 underline">Retry</button></p>}
    {!!data?.unavailable?.length&&<p role="status">Partial inbox: {data.unavailable.join(', ')} unavailable.</p>}
    <div className="grid items-start gap-3 md:grid-cols-[minmax(14rem,1fr)_minmax(0,2fr)]">
      <div className={(selected?'hidden md:block ':'')+'rounded-xl bg-[#151f30] p-3'}>
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-[#0B0E14] px-3"><Search aria-hidden="true" size={16}/>
          <input aria-label="Search teams" placeholder="Search teams…" className="min-h-11 min-w-0 flex-1 bg-transparent text-base" value={search} onChange={e=>setSearch(e.target.value)}/>
          {search&&<button className="min-h-11" aria-label="Clear search" onClick={()=>setSearch('')}><X size={16}/></button>}</div>
        {!data&&!error&&<p role="status">Loading conversations…</p>}
        {!matching.length&&<p className="py-4 text-sm text-gray-300">{search?'No matching teams.':'No website teams available.'}</p>}
        <div className="max-h-[60dvh] overflow-y-auto">{matching.map(team=>{
          const t=activity.get(team.id);
          return <button key={team.id} type="button" aria-pressed={selected===team.id}
            className={'flex min-h-16 w-full items-center gap-3 rounded-lg p-2 text-left '+(selected===team.id?'bg-white/10':'hover:bg-white/5')}
            onClick={()=>setSelected(team.id)}><ChatAvatar brand={team}/>
            <span className="min-w-0 flex-1"><strong className="block truncate">{team.name||team.slug}</strong>
              <span className="text-xs text-gray-300">{t?(t.side==='finance'?'Finance replied':'Team messaged')+' · '+stamp(t.created):'Start conversation'}</span></span>
            {!!t?.unread&&<span className="shrink-0 rounded-full bg-amber-300 px-2 text-xs font-bold text-black" aria-label={t.unread+' unread'}>{t.unread}</span>}
          </button>;})}</div>
      </div>
      {selected?<DepartmentThread key={selected} client={client} finance tenant={selected} brand={directory.get(selected)} onBack={()=>setSelected('')}/>:<p className="hidden self-center text-center text-sm text-gray-300 md:block">Choose a team to open its conversation.</p>}
    </div></section>;
}
